import { inputClass } from '../AuthCard.tsx';
import type { EditProps, ViewProps } from './types.ts';

type Row = { name: string; note?: string } & ({ role: string } | { kind: string });

function PeopleList({ title, rows }: { title: string; rows: Row[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="break-inside-avoid">
      <h4 className="mb-1.5 text-xs font-semibold tracking-wide text-gray-500 uppercase">{title}</h4>
      <ul className="space-y-1.5">
      {rows.map((row, i) => (
        <li key={i} className="text-sm">
          <span className="font-semibold text-gray-800">{row.name}</span>
          <span className="ml-2 text-gray-500">{'role' in row ? row.role : row.kind}</span>
          {row.note && <span className="mt-0.5 block text-xs text-gray-500">{row.note}</span>}
        </li>
      ))}
      </ul>
    </div>
  );
}

export function TeamListView({ content }: ViewProps<'team_list'>) {
  if (content.members.length === 0 && content.partners.length === 0) return null;
  return (
    <div className="space-y-4">
      <PeopleList title="Founding team & management" rows={content.members} />
      <PeopleList title="Strategic partners" rows={content.partners} />
    </div>
  );
}

export function TeamListEdit({ content, onChange }: EditProps<'team_list'>) {
  const setMember = (i: number, patch: Partial<(typeof content.members)[number]>) =>
    onChange({
      ...content,
      members: content.members.map((m, j) => (j === i ? { ...m, ...patch } : m)),
    });
  const setPartner = (i: number, patch: Partial<(typeof content.partners)[number]>) =>
    onChange({
      ...content,
      partners: content.partners.map((p, j) => (j === i ? { ...p, ...patch } : p)),
    });

  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">Team</h4>
        <div className="space-y-2">
          {content.members.map((member, i) => (
            <div key={i} className="flex gap-2">
              <input className={inputClass} placeholder="Name" value={member.name}
                onChange={(e) => setMember(i, { name: e.target.value })} />
              <input className={inputClass} placeholder="Role" value={member.role}
                onChange={(e) => setMember(i, { role: e.target.value })} />
              <input className={inputClass} placeholder="Credentials (optional)" value={member.note ?? ''}
                onChange={(e) => setMember(i, { note: e.target.value || undefined })} />
              <button type="button" className="text-sm whitespace-nowrap text-red-500 hover:underline"
                onClick={() => onChange({ ...content, members: content.members.filter((_, j) => j !== i) })}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="text-sm text-blue-600 hover:underline"
            onClick={() => onChange({ ...content, members: [...content.members, { name: '', role: '' }] })}>
            Add team member
          </button>
        </div>
      </div>
      <div>
        <h4 className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">Partners</h4>
        <div className="space-y-2">
          {content.partners.map((partner, i) => (
            <div key={i} className="flex gap-2">
              <input className={inputClass} placeholder="Name" value={partner.name}
                onChange={(e) => setPartner(i, { name: e.target.value })} />
              <input className={inputClass} placeholder="Kind (supplier, bank…)" value={partner.kind}
                onChange={(e) => setPartner(i, { kind: e.target.value })} />
              <input className={inputClass} placeholder="Note (optional)" value={partner.note ?? ''}
                onChange={(e) => setPartner(i, { note: e.target.value || undefined })} />
              <button type="button" className="text-sm whitespace-nowrap text-red-500 hover:underline"
                onClick={() => onChange({ ...content, partners: content.partners.filter((_, j) => j !== i) })}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="text-sm text-blue-600 hover:underline"
            onClick={() => onChange({ ...content, partners: [...content.partners, { name: '', kind: '' }] })}>
            Add partner
          </button>
        </div>
      </div>
    </div>
  );
}
